import React, { useState } from 'react';
import { BiPencil, BiPlus } from 'react-icons/bi';
import DeleteProfileDetails from './DeleteProfileDetails';
import { convertTimeToMonthYearString } from 'shared-utils';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import { UIUserEducationalDetail, UserEducationalDetail } from 'types';
import EditProfileEducationModal from './EditProfileEducationModal';
import { useMutation, useQueryClient } from 'react-query';
import { useToast } from 'src/context/ToastProvider';
import { deleteUserEducationFn } from 'src/apis/profile';
import ProfileContentHeader from './ProfileContentHeader';

interface Props {
	title: string;
	editing?: boolean;
	userEducation?: UIUserEducationalDetail[];
	profileId?: string;
	id?: string;
	isSelf?: boolean | null;
	isPublicProfile: boolean;
}

const ProfileEducation = ({ title, isSelf, isPublicProfile, editing = false, userEducation, profileId, id }: Props) => {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const queryClient = useQueryClient();
	const toast = useToast();
	const [deletingEducationIndex, setDeletingEducationIndex] = useState(-1);
	const [deleteModalStates, setDeleteModalStates] = useState<Array<boolean>>(userEducation?.map(() => false) ?? []);
	const [isEditing, setIsEditing] = useState(false);
	const [userEducationDetails, setUserEducationDetails] = useState<UserEducationalDetail>({
		collegeName: '',
		degreeName: '',
		description: '',
		fieldOfStudy: '',
		gradeType: '',
		gradeValue: 0,
		endedAt: new Date(),
		startedFrom: new Date(),
		collagelocation: '',
	});
	const [selectEducationId, setSelectedEducationId] = useState<string>('');

	const deleteEducationalDetailFn = useMutation({
		mutationKey: 'deleteEducationDetail',
		mutationFn: deleteUserEducationFn,
		onSuccess() {
			queryClient.refetchQueries(['getUserProfile']);
			window.scrollTo({ top: 0, behavior: 'smooth' });
			toast({
				title: 'Education details deleted successfully',
				type: 'success',
			});

			const newStates = [...deleteModalStates];
			newStates[deletingEducationIndex] = false;
			setDeleteModalStates(userEducation?.map(() => false) ?? []);
			setDeletingEducationIndex(-1);
		},
		onError(e: Error) {
			toast({
				title: e.message,
				type: 'error',
			});
		},
	});

	const handleEducationalDetailsDelete = (educationId: string, index: number) => {
		setDeletingEducationIndex(index);

		deleteEducationalDetailFn.mutate(educationId);
	};

	const handleEducation = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		e.stopPropagation();
		setIsEditing(false);
		setUserEducationDetails({
			collegeName: '',
			degreeName: '',
			fieldOfStudy: '',
			description: '',
			gradeType: '',
			gradeValue: 0,
			endedAt: new Date(),
			startedFrom: new Date(),
			collagelocation: '',
		});
		onOpen();
	};

	return (
		<>
			<ProfileContentHeader
				data={{ name: title, isImp: false }}
				extraContent={
					isSelf ? (
						<div className="ml-auto flex grow-0 items-center justify-end bg-transparent pb-1 mr-1 mt-1">
							{/* Add new education button */}
							<div
								onClick={event => handleEducation(event)}
								className="flex items-center justify-center gap-[4px] rounded-[4px] border border-[#C7C9D9] px-[4px] py-[2px] text-[11px] text-[#111928] dark:text-[white] hover:cursor-pointer hover:border-[black] bg-white dark:bg-inherit dark:hover:bg-[white] hover:!text-[black]"
							>
								<BiPlus size={15} />
							</div>
						</div>
					) : null
				}
			/>
			<div
				id={id}
				className="flex flex-col gap-2 overflow-hidden bg-transparent bg-white dark:bg-[#1F2A37] rounded-[8px] relative"
			>
				{userEducation && userEducation.length !== 0 ? (
					<div className="flex grow flex-col gap-[5px] px-1">
						{userEducation
							?.filter(uE => uE.degreeName || uE.collegeName)
							.map((eductaion: UIUserEducationalDetail, index: number) => {
								const educationFrom = eductaion?.startedFrom
									? convertTimeToMonthYearString(eductaion?.startedFrom)
									: '';
								const educationTo = eductaion?.endedAt ? convertTimeToMonthYearString(eductaion?.endedAt) : '';
								return (
									<div
										key={eductaion._id}
										className="p-3 dark:bg-gray-800 md:group-hover:show-icons-on-hover group w-full whitespace-normal break-words font-inter"
									>
										<div className="flex justify-between">
											<p className="max-w-[90%] whitespace-normal break-words text-[16px] font-[500] text-[#28293D] dark:text-[#F2F2F5]">
												{eductaion.degreeName
													? eductaion.fieldOfStudy
														? `${eductaion.degreeName} - ${eductaion.fieldOfStudy}`
														: `${eductaion.degreeName ? eductaion.degreeName : ''}`
													: eductaion.collegeName ?? ''}
											</p>

											{isSelf ? (
												<div className="edit-delete-icon text-[#111928] dark:text-[#c2c3db] sm:flex sm:gap-2 md:hidden md:group-hover:flex md:group-hover:gap-3">
													<div>
														<BiPencil
															onClick={e => {
																e.stopPropagation();
																setSelectedEducationId(eductaion._id);
																setUserEducationDetails({
																	collegeName: eductaion.collegeName || '',
																	degreeName: eductaion.degreeName || '',
																	description: eductaion.description || '',
																	fieldOfStudy: eductaion.fieldOfStudy || '',
																	gradeType: eductaion.gradeType || '',
																	gradeValue: eductaion.gradeValue || 0,
																	endedAt: eductaion.endedAt || new Date(),
																	collagelocation: eductaion.collagelocation || '',
																	startedFrom: eductaion.startedFrom || new Date(),
																});
																setIsEditing(true);
																onOpen();
															}}
															className="cursor-pointer"
														/>
													</div>
													<div>
														<DeleteProfileDetails
															onDelete={handleEducationalDetailsDelete}
															id={eductaion._id}
															index={index}
															isDeleteModalOpen={deleteModalStates[index]}
															setIsDeleteModalOpen={isOpen => {
																const newStates = [...deleteModalStates];
																newStates[index] = isOpen;
																setDeleteModalStates(newStates);
															}}
															isLoading={false}
														/>
													</div>
												</div>
											) : null}
										</div>
										{eductaion.collegeName && eductaion.degreeName ? (
											<p
												className={`${
													editing ? 'max-w-[90%]' : 'max-w-[100%]'
												} whitespace-normal break-words text-[14px] font-[400] text-[#111928] dark:text-[#E4E4EB]`}
											>
												{eductaion.collegeName}
											</p>
										) : null}
										<div
											className={`${
												editing ? 'max-w-[90%]' : 'max-w-[100%]'
											} flex w-full gap-[4px] text-[12px] font-[400] text-[#111928] dark:text-[#E4E4EB]`}
										>
											{eductaion.collagelocation ? (
												<p className="truncate sm:max-w-[50%] md:max-w-[30%]">{eductaion.collagelocation}</p>
											) : null}
											{educationTo || educationFrom ? (
												<ul className="list-disc truncate pl-[20px] sm:pl-[14px]">
													<li>{`${educationFrom} ${educationTo && educationFrom ? `- ${educationTo}` : ''}`}</li>
												</ul>
											) : null}
										</div>
									</div>
								);
							})}
					</div>
				) : (
					<div className="px-4 py-5 text-center font-inter text-[14px]  font-[500] text-[#28293D] dark:text-[#C7C9D9]">
						{isSelf && !isPublicProfile
							? 'Add your education details and let the world know about your coding adventures in the classroom.'
							: "There's nothing to show."}
					</div>
				)}
				<EditProfileEducationModal
					isOpen={isOpen}
					onClose={onClose}
					educationDetails={userEducationDetails}
					isEditing={isEditing}
					selectedEducationId={selectEducationId}
				/>
			</div>
		</>
	);
};

export default ProfileEducation;
